const API_BASE = "/api";

export interface DailyStatus {
  day: string;
  /** The streak you'd have after claiming today (or your current streak if claimed). */
  streak: number;
  /** Flies awarded for claiming today. */
  reward: number;
  claimable: boolean;
}

/**
 * Thin API client + cache for the daily login reward. Status only changes at the
 * UTC day rollover, so it's refreshed on startup and when the menu opens (no
 * background poll). The menu shows a badge while `claimable()`.
 */
export class Daily {
  private status: DailyStatus | null = null;

  get(): DailyStatus | null {
    return this.status;
  }
  claimable(): boolean {
    return !!this.status?.claimable;
  }
  clear(): void {
    this.status = null;
  }

  async refresh(name: string, token: string): Promise<void> {
    if (!name || !token) return;
    try {
      const res = await fetch(`${API_BASE}/daily?name=${encodeURIComponent(name)}`, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const d = (await res.json()) as Partial<DailyStatus>;
      this.status = {
        day: String(d.day ?? ""),
        streak: Math.max(0, Number(d.streak) || 0),
        reward: Math.max(0, Number(d.reward) || 0),
        claimable: !!d.claimable,
      };
    } catch {
      // keep cache on network error
    }
  }

  /** Claim today's reward. Resolves the flies awarded + new streak/wallet, or null. */
  async claim(
    name: string,
    token: string,
  ): Promise<{ reward: number; streak: number; wallet: number } | null> {
    try {
      const res = await fetch(`${API_BASE}/daily/claim`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify({ name }),
      });
      const d = (await res.json().catch(() => ({}))) as Partial<DailyStatus> & {
        ok?: boolean;
        reward?: number;
        streak?: number;
        wallet?: number;
      };
      if (!res.ok || !d.ok) return null;
      if (this.status) {
        this.status = {
          ...this.status,
          streak: Number(d.streak) || this.status.streak,
          reward: Number(d.reward) || this.status.reward,
          claimable: false,
        };
      }
      return {
        reward: Math.max(0, Number(d.reward) || 0),
        streak: Math.max(0, Number(d.streak) || 0),
        wallet: Math.max(0, Number(d.wallet) || 0),
      };
    } catch {
      return null;
    }
  }
}
